import type { StandingEntry } from './useStandings'

export type RankTier = 'lead' | 'podium' | 'top10' | 'close' | 'chasing'

export interface RankOutlook {
  tier: RankTier
  /** Points to the tier's target; the lead over the runner-up when tier is 'lead'. */
  gap: number
  /** Level with first, or alone in the standings — there is no lead to report. */
  tied: boolean
}

/**
 * What it would take to move up.
 *
 * The banner this feeds replaces one that only ever said where you stood, which
 * is why every tier here resolves to a named target and the points between you
 * and it. Targets come out of the top ten already on screen, so the arithmetic
 * is one the member can check against the table underneath.
 *
 * Targets are chosen by comparing scores, never by indexing the array: the
 * standings rank with RANK() OVER (ORDER BY total_score DESC), so tied climbers
 * share a rank and `standings[rank - 2]` is not reliably the climber above you.
 */
export function rankOutlook(myEntry: StandingEntry, standings: StandingEntry[]): RankOutlook {
  const mine = myEntry.totalScore
  const rank = myEntry.rank

  if (rank <= 1) {
    const others = standings.filter((e) => e.userId !== myEntry.userId)
    // Someone level with you, or nobody behind you at all. Both read the same
    // to a leader: there is no margin to defend, so neither gets a lead figure.
    if (others.some((e) => e.totalScore === mine)) return { tier: 'lead', gap: 0, tied: true }
    const below = others.map((e) => e.totalScore).filter((score) => score < mine)
    if (below.length === 0) return { tier: 'lead', gap: 0, tied: true }
    return { tier: 'lead', gap: mine - Math.max(...below), tied: false }
  }

  if (rank <= 3) {
    const first = Math.max(...standings.map((e) => e.totalScore), mine)
    return { tier: 'podium', gap: first - mine, tied: false }
  }

  if (rank <= 10) {
    // The podium's lowest score, which is what you have to pass to stand on it.
    const podium = standings.filter((e) => e.rank <= 3).map((e) => e.totalScore)
    const third = podium.length > 0 ? Math.min(...podium) : mine
    return { tier: 'top10', gap: Math.max(0, third - mine), tied: false }
  }

  // Outside the table: the target is the last row of it. The list is capped at
  // ten server-side, and a rank past ten guarantees it is full.
  const tenth = standings[standings.length - 1]?.totalScore ?? mine
  const gap = Math.max(0, tenth - mine)
  return { tier: rank <= 15 ? 'close' : 'chasing', gap, tied: false }
}
